import { createContext, useContext, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, ConfirmModal } from '@safetyhub/ui';
import {
  HEALTH_EXAM_RECEIPT_OBLIGATION_KEYS,
  LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL,
  exemptionBasisDefinition,
  formatDateToLocal,
  formatObligationRecurrence,
  type LegalObligationEventView,
  type LegalObligationPendingResultView,
  type LegalObligationRow,
  type LegalObligationSubjectView,
  type SiteWorkTypesResponse,
} from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import {
  createLegalObligationExclusion,
  getLegalObligations,
  legalDocumentsQueryKeys,
  revokeLegalObligationExclusion,
} from '../../lib/legalDocuments';
import {
  APPLICABILITY_BADGE,
  INSTANCE_STATUS_LABEL,
  describeIntervalConfirmation,
  describeMissingInstance,
  describeSiteManagement,
  describeLatestResult,
  describeMeasurementResult,
  exclusionReasonLabel,
  isRollingRecurrence,
  SUBJECT_EXCLUDED_HINT,
  subjectExcludedText,
  type ExclusionReasonCode,
} from '../../lib/legalDocumentLabels';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../stores/auth';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { useToastStore } from '../../stores/toast';
import { Tooltip } from '../../components/common/Tooltip';
import { ChevronLeftIcon } from '../../components/icons';
import ExclusionModal from '../../components/legal-documents/ExclusionModal';
import EvidenceModal, {
  type EvidenceSharedTarget,
  type EvidenceSubmitInput,
} from '../../components/legal-documents/EvidenceModal';
import EvidenceListModal from '../../components/legal-documents/EvidenceListModal';
import AnnualGrid from '../../components/legal-documents/AnnualGrid';
import {
  AgencyAdditionsCard,
  AgencyOverlayNote,
  agencyDisplayTitle,
} from '../../components/legal-documents/AgencyOverlayNote';
import EventRegisterModal, {
  type EventRegisterInput,
} from '../../components/legal-documents/EventRegisterModal';
import LastPerformedModal, {
  isoToLocalDate,
  type LastPerformedInput,
} from '../../components/legal-documents/LastPerformedModal';
import SiteLegalFactsPanel from '../../components/legal-documents/SiteLegalFactsPanel';
import { getSiteWorkTypes, legalSiteFactsQueryKeys } from '../../lib/legalSiteFacts';
import ResultEvidenceListModal from '../../components/legal-documents/ResultEvidenceListModal';
import ResultRecordModal, { describeResultRecorded } from '../../components/legal-documents/ResultRecordModal';
import HealthExamReceiptModal, {
  describeHealthExamReceiptRecorded,
} from '../../components/legal-documents/HealthExamReceiptModal';
import { acceptsSiteLevelResult } from '../../lib/legalObligationResultInput';
import { specialExamShortenCandidates } from '../../lib/specialExamShorten';
import {
  attachLegalObligationEvidence,
  attachLegalObligationResultEvidence,
  uploadLegalObligationResultEvidenceFile,
  legalObligationEvidenceQueryKeys,
  materializeLegalObligationInstance,
  registerLegalObligationEvent,
  revokeLegalObligationEvent,
  setLegalObligationLastPerformed,
  uploadLegalObligationEvidenceFile,
  uploadLegalObligationExemption,
} from '../../lib/legalObligationEvidence';
import ExemptionModal, { type ExemptionSubmitInput } from '../../components/legal-documents/ExemptionModal';

/**
 * 한 현장의 법정 의무 목록 — 제외 등록·해제 + **증빙 연결·업로드·소급 기록**(9-d · L3·L5·L7·L13 —
 * 결정 ⓔ *고객 화면 입구* #3의 「다음 라운드」가 이것이다).
 *
 * `?obligationId=<id>`로 들어오면 그 행을 강조하고 스크롤한다 — 알림(임박·지연)의 목적지다.
 * `?view=annual`이면 같은 현장의 **연간 격자**(L2 · `components/legal-documents/AnnualGrid`)를 그린다.
 *
 * 제외 버튼은 **현재 작업 조직** 멤버십의 `obligation.exempt`로, 증빙 추가는 `obligation.manage`로 가린다 —
 * 서버도 같은 조직(요청의 `companyId` query)의 자격으로 판정하고, 그 조직이 현장의 소유 조직이거나 현재
 * 수검업체일 때만 접근이 성립한다(팀장 확정 2026-10-28). 그래도 서버가 거부하면(403 등) 모달은 열린 채
 * 문구를 보이고 잠기지 않는다 — 그 자리는 spec이 잠근다.
 *
 * 증빙은 **인스턴스**에 붙는다. 「지금 기간」 행이 아직 실체화되지 않았으면(`currentInstance: null` — 크론이
 * 기한이 다가올 때 만든다) 증빙 추가가 먼저 온디맨드 실체화를 부른다(같은 멱등 입구). 기간이 정해지지 않는
 * 의무(`ONCE` 등)는 서버가 400으로 사유를 돌려주고 토스트로 보인다.
 * 사건 문서(`ON_EVENT`)는 「사건 등록」이 입구이고 사건마다 항목이 생긴다(9-i). 기준일부터 도는 주기(`PERIODIC · ROLLING` —
 * 종전 `MULTI_YEAR`)는 「마지막 실시일 입력」(또는 설치·배치일 — ②-b D6)이 이번 주기를 세운다(9-l) — 둘 다 2026-10-29 사용자 결정 「A안」.
 * 사건 기한은 카탈로그 오프셋이 있으면 미리 채운다(②-b D5) · 업종 변형의 「확인 필요」는 현장 업종 입력으로 안내한다(D3-b ⓐ′).
 *
 * `applicability === 'UNKNOWN'`인 의무는 「해당 여부 확인 필요」로 표시하되 목록에 남긴다(사용자 결정:
 * 포함하되 단정하지 않는다) — 업체가 그 행에서 제외(해당 없음)로 닫을 수 있다.
 */
export default function SiteObligationsPage() {
  const { siteId = '' } = useParams<{ siteId: string }>();
  const companyId = useAuthStore((s) => s.currentMembership?.companyId) ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const focusObligationId = searchParams.get('obligationId');
  // 「이번 기간」(기본 · 알림 목적지) ↔ 「연간 보기」(L2). URL 에 두어 새로고침·공유에도 유지한다
  const view: 'period' | 'annual' = searchParams.get('view') === 'annual' ? 'annual' : 'period';
  const switchView = (next: 'period' | 'annual') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'annual') params.set('view', 'annual');
    else params.delete('view');
    setSearchParams(params, { replace: true });
  };
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canExempt = can('obligation.exempt');
  const canManage = can('obligation.manage');
  const deviceLayout = useDeviceLayout();
  const isDesktop = deviceLayout === 'desktop';
  const { showToast } = useToastStore();

  const queryKey = legalDocumentsQueryKeys.obligations(companyId, siteId);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getLegalObligations(companyId, siteId),
    enabled: !!siteId && !!companyId,
  });

  const [excludeTarget, setExcludeTarget] = useState<LegalObligationRow | null>(null);
  const [excludeError, setExcludeError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<LegalObligationRow | null>(null);

  const excludeMutation = useMutation({
    mutationFn: (input: { obligationId: string; reasonCode: ExclusionReasonCode; note?: string }) =>
      createLegalObligationExclusion(companyId, siteId, input),
    onSuccess: async () => {
      setExcludeTarget(null);
      showToast('해당 없음으로 제외했습니다.', 'success');
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => setExcludeError(apiErrorMessage(err) ?? '제외 등록에 실패했습니다.'),
  });

  const revokeMutation = useMutation({
    mutationFn: (exclusionId: string) =>
      revokeLegalObligationExclusion(companyId, siteId, exclusionId),
    onSuccess: async () => {
      setRevokeTarget(null);
      showToast('제외를 해제했습니다.', 'success');
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      // 확인 모달은 닫는다 — 열어 두면 같은 요청을 다시 누르는 것 말고 할 일이 없다(막다른 길)
      setRevokeTarget(null);
      showToast(apiErrorMessage(err) ?? '제외 해제에 실패했습니다.', 'error');
    },
  });

  // ── 증빙(9-d) ──
  /** 증빙 붙이기 모달의 대상 — 인스턴스가 정해진 뒤에만 연다(없으면 아래 실체화가 먼저) */
  const [evidenceTarget, setEvidenceTarget] = useState<{
    row: LegalObligationRow;
    instanceId: string;
    /** 사건 등록으로 생긴 항목(9-i) — 「사건 없음」 선택지를 숨긴다 */
    eventInstance?: boolean;
    /** 대상 줄에서 연 경우(2026-11-04) — 그 판정 단위의 결과표 없는 결과(현장 단위 줄의 것이 아니다) · 「사건 없음」도 숨긴다 */
    subject?: LegalObligationSubjectView;
  } | null>(null);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  /** 증빙 목록 모달의 대상 — 인스턴스가 있는 행만 */
  const [evidenceListTarget, setEvidenceListTarget] = useState<{
    row: LegalObligationRow;
    instanceId: string;
    /**
     * 사건 항목의 목록(9-i) — 「문서 등록」(onAttachMore)이 이 표식을 붙이기 모달로 그대로 넘긴다. 종전엔 표식이 없어 사건 항목에서
     * 「해당 기간 사건 없음」·결과표 연결이 다시 보였다(서버 400 · 2026-11-03 S18)
     */
    eventInstance?: boolean;
  } | null>(null);

  const invalidateEvidence = async (instanceId: string) => {
    await queryClient.invalidateQueries({ queryKey });
    await queryClient.invalidateQueries({
      queryKey: legalObligationEvidenceQueryKeys.evidences(companyId, siteId, instanceId),
    });
  };

  const materializeMutation = useMutation({
    mutationFn: (row: LegalObligationRow) => materializeLegalObligationInstance(companyId, siteId, row.obligationId),
    onSuccess: async (result, row) => {
      setEvidenceError(null);
      setEvidenceTarget({ row, instanceId: result.instanceId });
      if (result.created) await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => showToast(apiErrorMessage(err) ?? '이 문서의 기간을 만들지 못했습니다.', 'error'),
  });

  const attachMutation = useMutation({
    mutationFn: async ({ instanceId, input }: { instanceId: string; input: EvidenceSubmitInput }) => {
      if (input.kind === 'EXTERNAL_FILE') {
        const { file, fulfilledAt, note, resultEntryId, alsoInstanceIds } = input;
        return uploadLegalObligationEvidenceFile(companyId, siteId, instanceId, {
          file,
          fulfilledAt,
          ...(note ? { note } : {}),
          ...(resultEntryId ? { resultEntryId } : {}),
          // Q14 ⓐ — 같은 파일을 함께 등록할 다른 항목
          ...(alsoInstanceIds && alsoInstanceIds.length > 0 ? { alsoInstanceIds } : {}),
        });
      }
      return attachLegalObligationEvidence(companyId, siteId, instanceId, input);
    },
    onSuccess: async (result, { instanceId }) => {
      setEvidenceTarget(null);
      const also = result.alsoAttached ?? [];
      // B+ — 결과표는 «그 측정이 든 기간»에 붙는다(요청한 이번 기간이 아닐 수 있다 · 응답 instanceId)
      showToast(
        result.instanceId !== instanceId
          ? '문서를 그 측정이 든 기간의 결과표로 등록했습니다.'
          : also.length > 0
            ? `문서를 ${also.length + 1}개 항목에 함께 등록했습니다.`
            : '문서를 등록했습니다.',
        'success'
      );
      await invalidateEvidence(instanceId);
      if (result.instanceId !== instanceId) await invalidateEvidence(result.instanceId);
      for (const a of also) {
        await queryClient.invalidateQueries({ queryKey: legalObligationEvidenceQueryKeys.evidences(companyId, siteId, a.instanceId) });
      }
    },
    onError: (err) => setEvidenceError(apiErrorMessage(err) ?? '문서 등록에 실패했습니다.'),
  });

  const openAttachEvidence = (row: LegalObligationRow) => {
    setEvidenceError(null);
    if (row.currentInstance) {
      setEvidenceTarget({ row, instanceId: row.currentInstance.instanceId });
    } else {
      materializeMutation.mutate(row);
    }
  };

  const openEvidenceList = (row: LegalObligationRow) => {
    if (row.currentInstance) setEvidenceListTarget({ row, instanceId: row.currentInstance.instanceId });
  };

  // ── 결과 단위 결과표(B+ 2차 · 사용자 확인 2026-11-02) — 측정 결과 줄(행·대상 줄)에서 붙이고 본다. 붙일 기간은 서버가 정한다 ──
  const [resultAttachTarget, setResultAttachTarget] = useState<{
    row: LegalObligationRow;
    pendingResults: LegalObligationPendingResultView[];
  } | null>(null);
  const [resultListTarget, setResultListTarget] = useState<{
    row: LegalObligationRow;
    entryId: string;
    pendingResults: LegalObligationPendingResultView[];
  } | null>(null);
  const resultAttachMutation = useMutation({
    mutationFn: async ({ entryId, input }: { entryId: string; input: EvidenceSubmitInput }) => {
      if (input.kind === 'EXTERNAL_FILE') {
        const { file, fulfilledAt, note } = input;
        return uploadLegalObligationResultEvidenceFile(companyId, siteId, entryId, { file, fulfilledAt, ...(note ? { note } : {}) });
      }
      if (input.kind === 'INTERNAL_REPORT') {
        const { reportId, fulfilledAt, note } = input;
        return attachLegalObligationResultEvidence(companyId, siteId, entryId, { reportId, fulfilledAt, ...(note ? { note } : {}) });
      }
      // 「사건 없음」은 결과표가 아니다 — 모달이 그 선택지를 보이지 않는다(allowNoEvent=false)
      throw new Error('결과표는 문서나 파일이어야 합니다.');
    },
    onSuccess: async (result) => {
      setResultAttachTarget(null);
      showToast('결과표를 등록했습니다. 그 측정이 든 기간의 기록으로 남습니다.', 'success');
      await invalidateEvidence(result.instanceId);
    },
    onError: (err) => setEvidenceError(apiErrorMessage(err) ?? '결과표 등록에 실패했습니다.'),
  });
  const resultActions: ResultEvidenceActions = {
    canManage,
    onAttach: (row, pendingResults) => {
      setEvidenceError(null);
      setResultAttachTarget({ row, pendingResults });
    },
    onOpen: (row, entryId, pendingResults) => setResultListTarget({ row, entryId, pendingResults }),
  };

  // ── 대상 줄(사람)의 문서 등록·면제(2026-11-04 · 신규 직무교육 사람 단위 기한 · 사람 단위 면제) ──
  // 대상별 의무는 현장 한 줄의 「문서 등록」이 없다 — 그 사람의 기간 인스턴스를 대상 키로 만든 뒤(멱등) 그 인스턴스에 붙인다
  const [exemptionTarget, setExemptionTarget] = useState<{ row: LegalObligationRow; subject: LegalObligationSubjectView; instanceId: string } | null>(null);
  const [exemptionError, setExemptionError] = useState<string | null>(null);
  const subjectInstanceMutation = useMutation({
    mutationFn: ({ row, subject }: { row: LegalObligationRow; subject: LegalObligationSubjectView; purpose: 'evidence' | 'exemption' }) =>
      subject.currentInstance
        ? Promise.resolve({ instanceId: subject.currentInstance.instanceId, periodKey: subject.currentInstance.periodKey, created: false })
        : materializeLegalObligationInstance(companyId, siteId, row.obligationId, undefined, subject.subjectKey),
    onSuccess: async (result, { row, subject, purpose }) => {
      if (purpose === 'evidence') {
        setEvidenceError(null);
        setEvidenceTarget({ row, instanceId: result.instanceId, subject });
      } else {
        setExemptionError(null);
        setExemptionTarget({ row, subject, instanceId: result.instanceId });
      }
      if (result.created) await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => showToast(apiErrorMessage(err) ?? '이 사람의 기간을 만들지 못했습니다.', 'error'),
  });
  const exemptionMutation = useMutation({
    mutationFn: ({ instanceId, input }: { instanceId: string; input: ExemptionSubmitInput }) =>
      uploadLegalObligationExemption(companyId, siteId, instanceId, input),
    onSuccess: async (_result, { instanceId }) => {
      setExemptionTarget(null);
      showToast('면제를 기록했습니다. 이 사람의 그 기간이 닫혔습니다.', 'success');
      await invalidateEvidence(instanceId);
    },
    onError: (err) => setExemptionError(apiErrorMessage(err) ?? '면제 등록에 실패했습니다.'),
  });
  const subjectActions: SubjectActions = {
    canManage,
    canExempt,
    onAttach: (row, subject) => subjectInstanceMutation.mutate({ row, subject, purpose: 'evidence' }),
    onExempt: (row, subject) => subjectInstanceMutation.mutate({ row, subject, purpose: 'exemption' }),
  };

  // ── 9-i 사건 발생 · 9-l 마지막 실시일 ──
  const [eventTarget, setEventTarget] = useState<LegalObligationRow | null>(null);
  // ②-c 2부 E6 — 사건 등록의 「대상 작업」 선택지(그 현장 작업 명부 ∩ 이 의무에 연결된 분류). 명부 패널과 같은 캐시 키
  const { data: workTypesData } = useQuery({
    queryKey: legalSiteFactsQueryKeys.workTypes(companyId, siteId),
    queryFn: () => getSiteWorkTypes(companyId, siteId),
    enabled: !!eventTarget && !!companyId && !!siteId,
  });
  const [eventError, setEventError] = useState<string | null>(null);
  const [revokeEventTarget, setRevokeEventTarget] = useState<{ row: LegalObligationRow; event: LegalObligationEventView } | null>(null);
  const [lastPerformedTarget, setLastPerformedTarget] = useState<LegalObligationRow | null>(null);
  const [lastPerformedError, setLastPerformedError] = useState<string | null>(null);
  /** ②-c1b — 현장 단위 결과 기록의 대상 행(측정 대상 쌍의 결과는 현장 사실 명부에서) */
  const [resultRecordTarget, setResultRecordTarget] = useState<LegalObligationRow | null>(null);
  /** M4 ⓐ — 건강진단 결과표 수령 기록의 대상 행(특수·수시·임시) */
  const [healthExamReceiptTarget, setHealthExamReceiptTarget] = useState<LegalObligationRow | null>(null);

  const registerEventMutation = useMutation({
    mutationFn: ({ row, input }: { row: LegalObligationRow; input: EventRegisterInput }) =>
      registerLegalObligationEvent(companyId, siteId, row.obligationId, input),
    onSuccess: async (result) => {
      setEventTarget(null);
      // D5 — 서버는 법정 기한보다 늦은 입력을 거부하지 않고 경고만 싣는다(변형 선택이 빠진 합법 입력을 막지 않으려고) · 숨기지 않는다
      showToast(
        result.laterThanLegalDeadline && result.legalDueAt
          ? `사건을 등록했습니다. 입력한 기한이 법정 기한(${formatDateToLocal(new Date(result.legalDueAt))})보다 늦습니다 — 확인하세요.`
          : '사건을 등록했습니다. 기한까지 문서를 등록하세요.',
        result.laterThanLegalDeadline ? 'error' : 'success',
      );
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => setEventError(apiErrorMessage(err) ?? '사건 등록에 실패했습니다.'),
  });

  const revokeEventMutation = useMutation({
    mutationFn: (entryId: string) => revokeLegalObligationEvent(companyId, siteId, entryId),
    onSuccess: async () => {
      setRevokeEventTarget(null);
      showToast('사건 등록을 취소했습니다.', 'success');
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      // 확인 모달은 닫는다 — 열어 두면 같은 요청을 다시 누르는 것 말고 할 일이 없다(막다른 길)
      setRevokeEventTarget(null);
      showToast(apiErrorMessage(err) ?? '사건 등록 취소에 실패했습니다.', 'error');
    },
  });

  const lastPerformedMutation = useMutation({
    mutationFn: ({ row, input }: { row: LegalObligationRow; input: LastPerformedInput }) =>
      setLegalObligationLastPerformed(companyId, siteId, row.obligationId, input),
    onSuccess: async (result) => {
      setLastPerformedTarget(null);
      // 정정으로 옛 주기 항목이 남았으면(임박 알림이 이미 나간 것) 숨기지 않고 알린다
      showToast(
        result.keptStaleInstanceIds.length > 0
          ? `마지막 실시일을 저장했습니다. 알림이 이미 나간 이전 주기 항목 ${result.keptStaleInstanceIds.length}건은 남겨 두었습니다.`
          : '마지막 실시일을 저장했습니다.',
        'success'
      );
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => setLastPerformedError(apiErrorMessage(err) ?? '마지막 실시일 저장에 실패했습니다.'),
  });

  const eventHandlers: EventHandlers = {
    onRegisterEvent: (row) => {
      setEventError(null);
      setEventTarget(row);
    },
    onAttachEventEvidence: (row, event) => {
      setEvidenceError(null);
      // 사건 항목에는 「사건 없음」을 고를 수 없다(서버도 400 — 사건이 «있었다»는 기록과 모순)
      setEvidenceTarget({ row, instanceId: event.instance.instanceId, eventInstance: true });
    },
    onOpenEventEvidences: (row, event) =>
      setEvidenceListTarget({ row, instanceId: event.instance.instanceId, eventInstance: true }),
    onRevokeEvent: (row, event) => setRevokeEventTarget({ row, event }),
    onSetLastPerformed: (row) => {
      setLastPerformedError(null);
      setLastPerformedTarget(row);
    },
    onRecordResult: (row) => setResultRecordTarget(row),
    onRecordHealthExamReceipt: (row) => setHealthExamReceiptTarget(row),
  };

  // 알림 목적지 — 목록이 그려진 뒤 한 번 스크롤한다
  const obligations = data?.obligations ?? [];
  /**
   * 현장 유효 시간대 — 서버가 기간·기한을 파생한 바로 그 값(`LegalObligationsResponse.timezone` · 2026-11-03 P2). 날짜만 고르는 입력과
   * 「오늘」이 이 시간대다. 빠지면(옛 응답 · 손 픽스처) 정책의 마지막 폴백인 서울(`pickSiteDefaultTimezone`의 끝)로 떨어진다
   */
  const siteTimezone = data?.timezone ?? 'Asia/Seoul';
  const hasFocusRow =
    !!focusObligationId && obligations.some((o) => o.obligationId === focusObligationId);
  useEffect(() => {
    if (!hasFocusRow || !focusObligationId) return;
    document
      .getElementById(obligationRowDomId(focusObligationId))
      ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [hasFocusRow, focusObligationId]);

  const openExclude = (row: LegalObligationRow) => {
    setExcludeError(null);
    setExcludeTarget(row);
  };

  const site = data?.site;

  return (
    <ResultEvidenceActionsContext.Provider value={resultActions}>
    <SubjectActionsContext.Provider value={subjectActions}>
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-0">
      <Link
        to="/legal-documents"
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-ink-500 hover:text-ink-900"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        현장 목록
      </Link>

      {isLoading ? (
        <div className="flex justify-center py-12" role="status" aria-label="불러오는 중">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error || !site ? (
        <div role="alert" className="rounded-card border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-700">
            {(error && apiErrorMessage(error)) ?? '문서 목록을 불러오지 못했습니다.'}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-5">
            <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">
              {site.siteName}
            </h1>
            <p className="mt-1 text-[15px] font-medium text-ink-500">
              {describeSiteManagement(site)}
              {site.auditedCompanyName ? ` · 수검업체 ${site.auditedCompanyName}` : ''}
            </p>
            {/* 미등록 배너는 걷었다(2026-10-29) — 「기간별 이행 기록이 만들어지지 않는다」가 거짓이 됐고(실체화는
                미등록 현장도 포함 · `describeMissingInstance` docblock), 「미등록」이라는 사실은 바로 위
                `describeSiteManagement`가 이미 말한다. */}
          </div>

          <div
            className="mb-3 inline-flex rounded-[10px] border border-ink-200 p-0.5"
            role="group"
            aria-label="보기 전환"
          >
            {(
              [
                ['period', '이번 기간'],
                ['annual', '연간 보기'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={view === key}
                onClick={() => switchView(key)}
                className={`rounded-[8px] px-3 py-1.5 text-sm font-bold ${
                  view === key ? 'bg-brand-600 text-white' : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {view === 'annual' ? (
            <AnnualGrid companyId={companyId} siteId={siteId} canManage={canManage} timezone={siteTimezone} />
          ) : obligations.length === 0 ? (
            <Card padding="none" radius="lg" elevation="md" className="py-12 text-center">
              <p className="text-sm font-medium text-ink-900">
                이 현장에 표시할 법정 서류가 없습니다
              </p>
            </Card>
          ) : isDesktop ? (
            <ObligationTable
              rows={obligations}
              showResponsible={site.management === 'AGENCY'}
              focusObligationId={focusObligationId}
              canExempt={canExempt}
              canManage={canManage}
              onExclude={openExclude}
              onRevoke={setRevokeTarget}
              onOpenEvidences={openEvidenceList}
              onAttachEvidence={openAttachEvidence}
              {...eventHandlers}
            />
          ) : (
            <ObligationCardList
              rows={obligations}
              showResponsible={site.management === 'AGENCY'}
              focusObligationId={focusObligationId}
              canExempt={canExempt}
              canManage={canManage}
              onExclude={openExclude}
              onRevoke={setRevokeTarget}
              onOpenEvidences={openEvidenceList}
              onAttachEvidence={openAttachEvidence}
              {...eventHandlers}
            />
          )}
          {/* 대행 기관 서식에만 있는 항목(2층 `ADDITION`) — 「이번 기간」 보기에만. 연간 격자는 아직 변형을 읽지 않는다 */}
          {view === 'period' && <AgencyAdditionsCard additions={data?.agencyAdditions ?? []} />}
          {/* ②-c 2부 — 현장 사실 명부(특별교육 대상 작업 · 작업환경측정 대상 공정 × 유해인자 · 측정 결과 기록) */}
          {view === 'period' && (
            <SiteLegalFactsPanel
              companyId={companyId}
              siteId={siteId}
              canManage={canManage}
              obligations={obligations}
              timezone={siteTimezone}
              onChanged={() => queryClient.invalidateQueries({ queryKey })}
            />
          )}
        </>
      )}

      {evidenceTarget && (
        <EvidenceModal
          key={evidenceTarget.instanceId}
          open
          companyId={companyId}
          siteId={siteId}
          obligationId={evidenceTarget.row.obligationId}
          obligationTitle={agencyDisplayTitle(evidenceTarget.row)}
          templateAvailable={evidenceTarget.row.templateAvailable}
          allowNoEvent={!evidenceTarget.eventInstance && !evidenceTarget.subject}
          // B+ — 사건 항목은 측정 결과와 잇지 않는다(서버도 400) · 현장 단위 줄의 결과표 없는 결과 · 대상 줄이면 그 판정 단위의 것
          pendingResults={
            evidenceTarget.eventInstance ? [] : (evidenceTarget.subject?.pendingResults ?? evidenceTarget.row.pendingResults)
          }
          resultVariants={evidenceTarget.row.intervalVariants}
          timezone={siteTimezone}
          // Q14 ⓐ — 사건 항목이면 같은 의무의 문서 없는 다른 사건 항목에 같은 파일을 함께 등록할 수 있다
          sharedTargets={evidenceTarget.eventInstance ? eventSharedTargets(evidenceTarget.row, evidenceTarget.instanceId) : []}
          submitting={attachMutation.isPending}
          errorMessage={evidenceError}
          onSubmit={(input) => {
            setEvidenceError(null);
            attachMutation.mutate({ instanceId: evidenceTarget.instanceId, input });
          }}
          onClose={() => setEvidenceTarget(null)}
        />
      )}

      {exemptionTarget && (
        <ExemptionModal
          key={exemptionTarget.instanceId}
          open
          bases={exemptionTarget.subject.exemptionBases ?? []}
          obligationTitle={agencyDisplayTitle(exemptionTarget.row)}
          subjectLabel={exemptionTarget.subject.label}
          timezone={siteTimezone}
          submitting={exemptionMutation.isPending}
          errorMessage={exemptionError}
          onSubmit={(input) => {
            setExemptionError(null);
            exemptionMutation.mutate({ instanceId: exemptionTarget.instanceId, input });
          }}
          onClose={() => setExemptionTarget(null)}
        />
      )}

      {resultAttachTarget && (
        <EvidenceModal
          open
          companyId={companyId}
          siteId={siteId}
          obligationId={resultAttachTarget.row.obligationId}
          obligationTitle={agencyDisplayTitle(resultAttachTarget.row)}
          templateAvailable={resultAttachTarget.row.templateAvailable}
          allowNoEvent={false}
          pendingResults={resultAttachTarget.pendingResults}
          resultVariants={resultAttachTarget.row.intervalVariants}
          introText="의 측정 결과표를 등록합니다. 결과표는 그 측정이 든 기간의 기록으로 남습니다(지금 기간이 아직 시작 전이어도 등록됩니다)."
          timezone={siteTimezone}
          submitting={resultAttachMutation.isPending}
          errorMessage={evidenceError}
          onSubmit={(input) => {
            setEvidenceError(null);
            const chosen = 'resultEntryId' in input && input.resultEntryId ? input.resultEntryId : resultAttachTarget.pendingResults[0]?.entryId;
            if (chosen) resultAttachMutation.mutate({ entryId: chosen, input });
          }}
          onClose={() => setResultAttachTarget(null)}
        />
      )}

      {resultListTarget && (
        <ResultEvidenceListModal
          key={resultListTarget.entryId}
          open
          companyId={companyId}
          siteId={siteId}
          entryId={resultListTarget.entryId}
          obligationTitle={agencyDisplayTitle(resultListTarget.row)}
          canManage={canWriteRow(canManage, resultListTarget.row)}
          pendingResults={resultListTarget.pendingResults}
          resultVariants={resultListTarget.row.intervalVariants}
          onResultRelinked={() => queryClient.invalidateQueries({ queryKey })}
          onClose={() => setResultListTarget(null)}
        />
      )}

      {evidenceListTarget && (
        <EvidenceListModal
          key={evidenceListTarget.instanceId}
          open
          companyId={companyId}
          siteId={siteId}
          instanceId={evidenceListTarget.instanceId}
          obligationTitle={agencyDisplayTitle(evidenceListTarget.row)}
          canManage={canWriteRow(canManage, evidenceListTarget.row)}
          // 사건 항목은 결과와 잇지 않는다(서버도 400) — 연결 바꾸기 선택지도 비운다
          pendingResults={evidenceListTarget.eventInstance ? [] : evidenceListTarget.row.pendingResults}
          resultVariants={evidenceListTarget.row.intervalVariants}
          onResultRelinked={() => queryClient.invalidateQueries({ queryKey })}
          onAttachMore={() => {
            const target = evidenceListTarget;
            setEvidenceListTarget(null);
            setEvidenceError(null);
            setEvidenceTarget(target);
          }}
          onClose={() => setEvidenceListTarget(null)}
        />
      )}

      {eventTarget && (
        <EventRegisterModal
          key={eventTarget.obligationId}
          open
          obligationKey={eventTarget.key}
          obligationTitle={agencyDisplayTitle(eventTarget)}
          eventDeadline={eventTarget.recurrenceRule.eventDeadline}
          variants={eventTarget.intervalVariants.filter((v) => v.target === 'EVENT_DEADLINE' && v.selector === 'EVENT_CHOICE')}
          choiceRequired={eventTarget.eventChoiceRequired === true}
          workTypeOptions={eventWorkTypeOptions(workTypesData, eventTarget.obligationId)}
          timezone={siteTimezone}
          submitting={registerEventMutation.isPending}
          errorMessage={eventError}
          onSubmit={(input) => {
            setEventError(null);
            registerEventMutation.mutate({ row: eventTarget, input });
          }}
          onClose={() => setEventTarget(null)}
        />
      )}

      {lastPerformedTarget && (
        <LastPerformedModal
          key={lastPerformedTarget.obligationId}
          open
          obligationTitle={agencyDisplayTitle(lastPerformedTarget)}
          rule={lastPerformedTarget.recurrenceRule}
          variants={lastPerformedTarget.intervalVariants.filter((v) => v.target === 'INITIAL' && v.selector === 'EVENT_CHOICE')}
          initialDate={
            lastPerformedTarget.rollingAnchor?.inputPerformedAt
              ? isoToLocalDate(lastPerformedTarget.rollingAnchor.inputPerformedAt, siteTimezone)
              : null
          }
          initialCycleStartDate={
            lastPerformedTarget.rollingAnchor?.cycleStartAt
              ? isoToLocalDate(lastPerformedTarget.rollingAnchor.cycleStartAt, siteTimezone)
              : null
          }
          initialCycleStartVariantId={lastPerformedTarget.rollingAnchor?.cycleStartVariantId ?? null}
          timezone={siteTimezone}
          submitting={lastPerformedMutation.isPending}
          errorMessage={lastPerformedError}
          onSubmit={(input) => {
            setLastPerformedError(null);
            lastPerformedMutation.mutate({ row: lastPerformedTarget, input });
          }}
          onClose={() => setLastPerformedTarget(null)}
        />
      )}

      {resultRecordTarget && (
        <ResultRecordModal
          key={resultRecordTarget.obligationId}
          companyId={companyId}
          siteId={siteId}
          row={resultRecordTarget}
          subjectKey=""
          subjectLabel={agencyDisplayTitle(resultRecordTarget)}
          timezone={siteTimezone}
          shortenCandidates={specialExamShortenCandidates(obligations, canManage, agencyDisplayTitle)}
          onClose={() => setResultRecordTarget(null)}
          onRecorded={async (result) => {
            setResultRecordTarget(null);
            const { message, tone } = describeResultRecorded(result);
            showToast(message, tone);
            await queryClient.invalidateQueries({ queryKey });
          }}
        />
      )}

      {healthExamReceiptTarget && (
        <HealthExamReceiptModal
          key={healthExamReceiptTarget.obligationId}
          companyId={companyId}
          siteId={siteId}
          row={healthExamReceiptTarget}
          title={agencyDisplayTitle(healthExamReceiptTarget)}
          timezone={siteTimezone}
          shortenCandidates={specialExamShortenCandidates(obligations, canManage, agencyDisplayTitle)}
          onClose={() => setHealthExamReceiptTarget(null)}
          onRecorded={async (result) => {
            setHealthExamReceiptTarget(null);
            showToast(describeHealthExamReceiptRecorded(result), 'success');
            // 보고 사건이 생기면 「사후관리 조치결과 보고」 행의 사건 줄이 바뀐다 — 목록 전체를 다시 읽는다
            await queryClient.invalidateQueries({ queryKey });
          }}
        />
      )}

      <ConfirmModal
        open={revokeEventTarget !== null}
        title="사건 등록을 취소할까요?"
        body={
          revokeEventTarget ? (
            <span>
              <b>{agencyDisplayTitle(revokeEventTarget.row)}</b>의 {formatDateToLocal(new Date(revokeEventTarget.event.occurredAt))} 사건
              항목이 사라집니다. 등록 기록은 이력으로 남습니다.
            </span>
          ) : undefined
        }
        confirmLabel="등록 취소"
        danger
        busy={revokeEventMutation.isPending}
        onConfirm={() => {
          const entryId = revokeEventTarget?.event.entryId;
          if (entryId) revokeEventMutation.mutate(entryId);
        }}
        onClose={() => {
          if (!revokeEventMutation.isPending) setRevokeEventTarget(null);
        }}
      />

      {excludeTarget && (
        <ExclusionModal
          key={excludeTarget.obligationId}
          open
          obligationTitle={agencyDisplayTitle(excludeTarget)}
          submitting={excludeMutation.isPending}
          errorMessage={excludeError}
          onSubmit={({ reasonCode, note }) => {
            setExcludeError(null);
            // 메모가 없으면 키 자체를 싣지 않는다(`note?: string` 계약 — undefined 키를 보내지 않는다)
            excludeMutation.mutate({
              obligationId: excludeTarget.obligationId,
              reasonCode,
              ...(note ? { note } : {}),
            });
          }}
          onClose={() => setExcludeTarget(null)}
        />
      )}

      <ConfirmModal
        open={revokeTarget !== null}
        title="제외를 해제할까요?"
        body={
          revokeTarget ? (
            <span>
              <b>{agencyDisplayTitle(revokeTarget)}</b> 문서가 다시 이 현장의 관리 대상이 됩니다. 제외했던 기록은
              이력으로 남습니다.
            </span>
          ) : undefined
        }
        confirmLabel="해제"
        danger
        busy={revokeMutation.isPending}
        onConfirm={() => {
          const exclusionId = revokeTarget?.exclusion?.exclusionId;
          if (exclusionId) revokeMutation.mutate(exclusionId);
        }}
        onClose={() => {
          if (!revokeMutation.isPending) setRevokeTarget(null);
        }}
      />
    </div>
    </SubjectActionsContext.Provider>
    </ResultEvidenceActionsContext.Provider>
  );
}

/**
 * 대상 줄(사람)의 동작(2026-11-04) — 그 사람의 기간에 문서 등록 · 교육 면제(근거 조항 + 증빙). 결과 줄과 같은 이유로 context다(행 렌더 여러 겹 아래).
 * 없으면(다른 호스트) 버튼을 그리지 않는다
 */
interface SubjectActions {
  canManage: boolean;
  canExempt: boolean;
  onAttach: (row: LegalObligationRow, subject: LegalObligationSubjectView) => void;
  onExempt: (row: LegalObligationRow, subject: LegalObligationSubjectView) => void;
}
const SubjectActionsContext = createContext<SubjectActions | null>(null);

/**
 * 결과 단위 결과표의 동작(B+ 2차) — 결과 줄(`LatestResultNote`)이 표·카드·대상 줄 깊이 어디서 그려지든 같은 동작을 쓰게 페이지가 주입한다
 * (그 줄들은 행 렌더 함수 여러 겹 아래라 prop 사슬 대신 context다). 없으면(다른 호스트) 버튼을 그리지 않는다
 */
interface ResultEvidenceActions {
  canManage: boolean;
  onAttach: (row: LegalObligationRow, pendingResults: LegalObligationPendingResultView[]) => void;
  onOpen: (row: LegalObligationRow, entryId: string, pendingResults: LegalObligationPendingResultView[]) => void;
}
const ResultEvidenceActionsContext = createContext<ResultEvidenceActions | null>(null);

/**
 * 사건 등록의 「대상 작업」 선택지(②-c 2부 E6) — 현장 작업 명부(활성) ∩ 그 의무의 범위(`eventScopes` — 정본은 조건 줄 `WORK_PERFORMED`의
 * `valueKeys`, 서버 `requireActiveWorkType`와 같은 함수가 만든다). 범위가 없는 의무는 빈 목록(칸을 그리지 않는다)
 */
function eventWorkTypeOptions(
  workTypes: Pick<SiteWorkTypesResponse, 'entries' | 'eventScopes'> | undefined,
  obligationId: string,
): { code: number; name: string }[] {
  const scope = workTypes?.eventScopes[obligationId];
  if (!workTypes || !scope) return [];
  return workTypes.entries
    .filter((e) => scope === 'ALL' || scope.includes(e.code))
    .map((e) => ({ code: e.code, name: e.name }));
}

/** 알림 목적지 스크롤의 DOM id — 표·카드 두 형태가 같은 규약을 쓴다 */
function obligationRowDomId(obligationId: string): string {
  return `legal-obligation-${obligationId}`;
}

/** 9-i 사건 · 9-l 마지막 실시일 — 표·카드 두 형태가 같은 묶음을 받는다 */
interface EventHandlers {
  onRegisterEvent: (row: LegalObligationRow) => void;
  onAttachEventEvidence: (row: LegalObligationRow, event: LegalObligationEventView) => void;
  onOpenEventEvidences: (row: LegalObligationRow, event: LegalObligationEventView) => void;
  onRevokeEvent: (row: LegalObligationRow, event: LegalObligationEventView) => void;
  onSetLastPerformed: (row: LegalObligationRow) => void;
  /** ②-c1b — 현장 단위 결과 기록(특수건강진단 등 · 입구 판정 `acceptsSiteLevelResult`) */
  onRecordResult: (row: LegalObligationRow) => void;
  /** M4 ⓐ — 건강진단 결과표 수령 기록(특수·수시·임시 · 입구 판정 shared `HEALTH_EXAM_RECEIPT_OBLIGATION_KEYS`) */
  onRecordHealthExamReceipt: (row: LegalObligationRow) => void;
}

interface RowListProps extends EventHandlers {
  rows: LegalObligationRow[];
  /**
   * 담당 주체 칸(「기관 몫」·「업체 몫」·「둘 중 하나」)을 그리나 — 대행(`AGENCY`) 현장만이다(사용자 결정 2026-10-29).
   * 그 칸이 답하는 질문은 「이 현장에서 누가 하나」이고 자체 관리·미등록 현장은 답이 늘 「우리」라, 카탈로그 값을
   * 그대로 그리면 기관이 없는 현장에 «기관 몫»이 뜬다. 카탈로그(admin)의 값은 그대로 둔다 — 표시만 가린다.
   */
  showResponsible: boolean;
  focusObligationId: string | null;
  canExempt: boolean;
  /** `obligation.manage` — 증빙 추가 버튼의 게이트 */
  canManage: boolean;
  onExclude: (row: LegalObligationRow) => void;
  onRevoke: (row: LegalObligationRow) => void;
  onOpenEvidences: (row: LegalObligationRow) => void;
  onAttachEvidence: (row: LegalObligationRow) => void;
}

function ObligationTable({
  rows,
  showResponsible,
  focusObligationId,
  canExempt,
  canManage,
  onExclude,
  onRevoke,
  onOpenEvidences,
  onAttachEvidence,
  ...handlers
}: RowListProps) {
  return (
    <Card padding="none" radius="lg" elevation="md" className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-ink-50 text-left text-xs font-bold text-ink-500">
          <tr>
            <th scope="col" className="px-4 py-3">
              문서명
            </th>
            {showResponsible && (
              <th scope="col" className="px-3 py-3">
                담당
              </th>
            )}
            <th scope="col" className="px-3 py-3">
              주기
            </th>
            <th scope="col" className="px-3 py-3">
              근거 법령
            </th>
            <th scope="col" className="px-3 py-3">
              이번 기간
            </th>
            <th scope="col" className="px-3 py-3 text-right">
              문서
            </th>
            <th scope="col" className="px-3 py-3">
              양식
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              <span className="sr-only">제외</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-200">
          {rows.map((row) => (
            <tr
              key={row.obligationId}
              id={obligationRowDomId(row.obligationId)}
              data-focused={row.obligationId === focusObligationId ? 'true' : undefined}
              className={`${row.obligationId === focusObligationId ? 'bg-brand-50' : ''} ${
                row.exclusion ? 'text-ink-400' : 'text-ink-700'
              }`}
            >
              <td className="px-4 py-3">
                <p className={`font-bold ${row.exclusion ? 'text-ink-500' : 'text-ink-900'}`}>
                  {agencyDisplayTitle(row)}
                </p>
                <AgencyOverlayNote row={row} />
                <ApplicabilityBadge row={row} />
                <WriteBlockNote row={row} canManage={canManage || canExempt} />
              </td>
              {showResponsible && (
                <td className="px-3 py-3 whitespace-nowrap">{responsibleLabel(row)}</td>
              )}
              <td className="px-3 py-3 whitespace-nowrap">{recurrenceLabel(row)}</td>
              <td className="px-3 py-3">{row.legalBasis ?? '—'}</td>
              <td className="px-3 py-3">
                <PeriodCell row={row} canManage={canWriteRow(canManage, row)} handlers={handlers} />
              </td>
              <td className="px-3 py-3 text-right tabular-nums">
                <EvidenceCell row={row} onOpenEvidences={onOpenEvidences} />
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <TemplateCell row={row} />
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <span className="inline-flex items-center justify-end gap-2">
                  <EvidenceAction row={row} canManage={canWriteRow(canManage, row)} onAttachEvidence={onAttachEvidence} handlers={handlers} />
                  <ExclusionAction
                    row={row}
                    canExempt={canWriteRow(canExempt, row)}
                    onExclude={onExclude}
                    onRevoke={onRevoke}
                  />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function ObligationCardList({
  rows,
  showResponsible,
  focusObligationId,
  canExempt,
  canManage,
  onExclude,
  onRevoke,
  onOpenEvidences,
  onAttachEvidence,
  ...handlers
}: RowListProps) {
  return (
    <ul className="space-y-3" aria-label="법정 서류 목록">
      {rows.map((row) => (
        <li key={row.obligationId} id={obligationRowDomId(row.obligationId)}>
          <Card
            padding="none"
            radius="lg"
            elevation="md"
            className={`p-4 ${row.obligationId === focusObligationId ? 'ring-2 ring-brand-300' : ''}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p
                  className={`text-sm font-bold ${row.exclusion ? 'text-ink-500' : 'text-ink-900'}`}
                >
                  {agencyDisplayTitle(row)}
                </p>
                <AgencyOverlayNote row={row} />
                <ApplicabilityBadge row={row} />
                <WriteBlockNote row={row} canManage={canManage || canExempt} />
              </div>
              <PeriodCell row={row} canManage={canWriteRow(canManage, row)} handlers={handlers} />
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              {showResponsible && (
                <>
                  <dt className="text-ink-500">담당</dt>
                  <dd className="text-ink-700">{responsibleLabel(row)}</dd>
                </>
              )}
              <dt className="text-ink-500">주기</dt>
              <dd className="text-ink-700">{recurrenceLabel(row)}</dd>
              <dt className="text-ink-500">근거 법령</dt>
              <dd className="text-ink-700">{row.legalBasis ?? '—'}</dd>
              <dt className="text-ink-500">문서</dt>
              <dd className="text-ink-700">
                <EvidenceCell row={row} onOpenEvidences={onOpenEvidences} />
              </dd>
              <dt className="text-ink-500">양식</dt>
              <dd className="text-ink-700">
                <TemplateCell row={row} />
              </dd>
            </dl>
            <div className="mt-3 flex justify-end gap-2">
              <EvidenceAction row={row} canManage={canWriteRow(canManage, row)} onAttachEvidence={onAttachEvidence} handlers={handlers} />
              <ExclusionAction
                row={row}
                canExempt={canWriteRow(canExempt, row)}
                onExclude={onExclude}
                onRevoke={onRevoke}
              />
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}

/**
 * 이 행에 쓰기 버튼(문서 등록·결과 기록·사건·기준일·제외·결과표)을 그리나 — 권한 키(`can()`) ∧ 서버가 실은 `writeBlock`이 없음.
 * `writeBlock`은 쓰기 입구의 403과 **같은 판정**(서버 `LegalDocumentsService.writeBlockOf`)이라 여기서 대행 분야를 다시 따지지 않는다.
 * 막히면 버튼을 «감춘다» — 대행 분야는 보는 사람이 풀 수 있는 막힘이 아니라 비활성으로 남겨도 갈 출구가 없다
 * (`WriteBlockNote`가 「왜 쓰기 버튼이 없나」를 한 줄로 말한다 · dev 실측 2026-11-02 「눌러야 403」).
 */
function canWriteRow(permitted: boolean, row: Pick<LegalObligationRow, 'writeBlock'>): boolean {
  return permitted && !row.writeBlock;
}

/** 쓰기가 막힌 행의 안내 한 줄 — 권한 키가 있는 사람에게만(없는 사람은 원래 버튼이 없어 이 안내가 질문에 답하지 않는다) */
function WriteBlockNote({ row, canManage }: { row: Pick<LegalObligationRow, 'writeBlock'>; canManage: boolean }) {
  if (!canManage || !row.writeBlock) return null;
  return (
    <p className="mt-0.5 text-xs text-ink-500" data-testid="write-block-note">
      {row.writeBlock.message}
    </p>
  );
}

function responsibleLabel(row: LegalObligationRow): string {
  return row.responsibleParty
    ? LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL[row.responsibleParty]
    : '—';
}

/** 주기 칸 — admin 카탈로그와 같은 shared 함수(다년 주기는 「3년마다」) */
function recurrenceLabel(row: LegalObligationRow): string {
  return formatObligationRecurrence(row) ?? '—';
}

/**
 * 이번 기간 칸 — 제외된 의무는 상태 대신 「제외됨」을 보인다(제외가 기간 상태를 덮는다).
 * 인스턴스가 없으면 **왜 없는지**를 가른다(`describeMissingInstance` — 주기 미정 · 사유 발생 시 · 마지막 실시일 확인
 * 필요 · 항목 생성 전 등). 종전의 한 문구 「기간 없음」은 그 상태들을 섞었다.
 * 사건 문서(`ON_EVENT`)는 등록된 사건 목록을, 기준일부터 도는 주기(`PERIODIC · ROLLING`)는 그 아래 기준일 한 줄을 더 그린다(9-i·9-l).
 */
function PeriodCell({
  row,
  canManage,
  handlers,
}: {
  row: LegalObligationRow;
  canManage: boolean;
  handlers: EventHandlers;
}) {
  if (row.exclusion) {
    return (
      <Badge tone="slate" size="sm">
        제외됨 · {exclusionReasonLabel(row.exclusion.reasonCode)}
      </Badge>
    );
  }
  if (row.recurrence === 'ON_EVENT' && row.events.length > 0) {
    return <EventList row={row} canManage={canManage} handlers={handlers} />;
  }
  // ②-c E4 — 사람 대상 의무는 현장 전체의 기간이 없고 대상(직위 배정된 사람)마다 한 줄이다
  if (row.currentPeriod.kind === 'subjects') {
    return (
      <span className="inline-flex flex-col items-start gap-0.5">
        <SubjectList row={row} />
        <IntervalConfirmationNote row={row} />
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <CurrentPeriodStatus row={row} />
      {row.rollingAnchor && (
        <span className="text-xs text-ink-400">
          {row.rollingAnchor.source === 'CYCLE_START' ? '설치·배치 ' : '마지막 실시 '}
          {formatDateToLocal(new Date(row.rollingAnchor.anchorAt))}
          {row.rollingAnchor.source === 'EVIDENCE' ? ' (등록 문서 기준)' : ''}
        </span>
      )}
      {/* ②-c E7 — 현장 단위 줄의 기록한 측정 결과(하위 줄과 같은 규칙) */}
      {row.latestResult && (
        <LatestResultNote
          testId="row-latest-result"
          className="text-xs"
          described={describeLatestResult(row.latestResult, row.intervalVariants)}
          row={row}
          entryId={row.latestResult.entryId}
          evidenceRegistered={row.latestResult.evidenceRegistered}
          pendingResults={row.pendingResults}
          linkedResults={row.linkedResults}
        />
      )}
      <IntervalConfirmationNote row={row} />
      {/* ②-c 2부 E7 ⓒ — 공정 × 유해인자 대상 의무인데 측정 대상이 0쌍: 현장 단위 한 줄(기본 간격)로 보이고 등록을 안내한다 */}
      {row.subjectNotice === 'MEASUREMENT_TARGET_REQUIRED' && (
        <Tooltip content="작업환경측정은 공정(단위작업장소) × 유해인자마다 주기가 따로 정해집니다(노출기준 초과 3개월 · 2회 연속 미만 1년). 아래 「작업환경측정 대상」에 등록하면 대상별 줄로 나뉩니다.">
          <span
            className="cursor-help text-xs text-amber-700 underline decoration-dotted underline-offset-2"
            data-testid="measurement-target-notice"
          >
            측정 대상(공정·유해인자) 등록 필요
          </span>
        </Tooltip>
      )}
    </span>
  );
}

/**
 * 간격 변형의 「확인 필요」(②-b D3-b ⓐ′) — 현장 업종이 없거나 얕아 **가장 짧은 간격을 보수 적용**한 행. 어느 깊이까지 고르면
 * 판정되는지 말하고 현장 정보(업종 칸)로 안내한다 — 입력 입구는 `SiteFormBody`의 `IndustryPicker`다.
 */
function IntervalConfirmationNote({ row }: { row: LegalObligationRow }) {
  const { siteId = '' } = useParams<{ siteId: string }>();
  const first = row.intervalConfirmations[0];
  if (!first) return null;
  const { label, hint } = describeIntervalConfirmation(first);
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs">
      <Tooltip content={hint}>
        <span className="cursor-help text-amber-700 underline decoration-dotted underline-offset-2">{label}</span>
      </Tooltip>
      <Link to={`/sites/${siteId}/edit`} className="font-semibold text-brand-600 underline decoration-dotted underline-offset-2">
        현장 업종 입력
      </Link>
    </span>
  );
}

/**
 * ②-c E4 — 사람 대상 의무의 **대상별 하위 줄**(설계 2차 §4 표 「web 목록 — 대상별 하위 줄(사람 이름)」). 「인원」은 줄 수다.
 * 대상이 0명이면 「직위 배정 필요」 — 해당 없음이 아니라 **입력이 아직 없는 상태**다(prod 실측 2026-11-02: 법정 직위 배정 3건뿐).
 * 입구는 조직 역할 화면(이 화면 범위 밖)이라 안내만 한다.
 */
function SubjectList({ row }: { row: LegalObligationRow }) {
  // ②-c2 — 설비 대상 의무인데 그 행으로 가는 설비가 없고 현장이 「설비 검토 완료」가 아니다(0대가 «해당 없음»인지 모른다)
  if (row.subjectNotice === 'EQUIPMENT_REVIEW_REQUIRED') {
    return (
      <Tooltip content="이 문서의 대상은 안전검사 대상 설비입니다. 현장 상세 「대상」에서 설비의 기종과 규격 값을 넣거나, 해당 설비가 없으면 「설비 검토 완료」를 표시하세요.">
        <span
          className="cursor-help text-xs text-amber-700 underline decoration-dotted underline-offset-2"
          data-testid="subject-notice"
        >
          설비 확인 필요
        </span>
      </Tooltip>
    );
  }
  if (row.subjectNotice === 'ROLE_ASSIGNMENT_REQUIRED' || row.subjects.length === 0) {
    return (
      <Tooltip content="이 문서의 대상은 법정 직위(안전관리자·보건관리자 등)가 배정된 사람입니다. 조직 역할에서 직위를 배정하면 대상이 생깁니다.">
        <span
          className="cursor-help text-xs text-amber-700 underline decoration-dotted underline-offset-2"
          data-testid="subject-notice"
        >
          직위 배정 필요
        </span>
      </Tooltip>
    );
  }
  return (
    <ul className="flex flex-col gap-0.5" data-testid="subject-list" aria-label={`${agencyDisplayTitle(row)} 대상`}>
      {row.subjects.map((subject) => (
        <li key={subject.subjectKey} className="flex flex-wrap items-center gap-1.5 text-xs" data-subject-key={subject.subjectKey}>
          <span className="font-semibold text-ink-700">{subject.label}</span>
          <SubjectStatus subject={subject} />
          <SubjectConfirmationNote subject={subject} />
          <SubjectActionButtons row={row} subject={subject} />
          {/* ②-c E7 — 기록한 측정 결과 · 그 결과표가 미등록이면 「결과표를 등록하면 완료」(서버 판정 `evidenceRegistered` · 함수 docblock) */}
          {subject.latestResult && (
            <LatestResultNote
              testId="subject-latest-result"
              described={describeLatestResult(subject.latestResult, row.intervalVariants)}
              row={row}
              entryId={subject.latestResult.entryId}
              evidenceRegistered={subject.latestResult.evidenceRegistered}
              pendingResults={subject.pendingResults}
              linkedResults={subject.linkedResults}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * 대상이 업체 의무의 분모 밖인 이유(사용자 결정 2026-11-02 11:43 ⓑ) — 위탁 직위의 직무교육은 수탁 기관의 의무다. 스위치는 서버
 * `DELEGATED_SUBJECT_POLICY`이고 이 라벨은 그 값이 `EXCLUDE`일 때만 보인다
 */
// 사유 문구·설명의 정본은 `lib/legalDocumentLabels.ts`(`SUBJECT_EXCLUDED_HINT` · `subjectExcludedText`) — 연간 격자와 한 벌

/** 기록한 측정 결과 한 줄 — 미이행 안내(`pendingEvidence`)면 강조색(문구의 정본은 `describeLatestResult`) */
function LatestResultNote({
  described,
  testId,
  className = '',
  row,
  entryId,
  evidenceRegistered,
  pendingResults,
  linkedResults,
}: {
  described: { text: string; pendingEvidence: boolean };
  testId: string;
  className?: string;
  /** B+ 2차 — 결과 단위 결과표 동작의 재료(그 줄의 행 · 최근 결과 id · 등록 판정 · 그 판정 단위의 결과표 없는 결과) */
  row: LegalObligationRow;
  entryId: string;
  evidenceRegistered: boolean;
  pendingResults: LegalObligationPendingResultView[];
  /** 그 판정 단위의 결과표가 이어진 결과(서버 `linkedResults`) — 최신 결과 «말고» 앞선 결과에도 「이어진 결과표 보기」를 둔다 */
  linkedResults: LegalObligationPendingResultView[];
}) {
  const actions = useContext(ResultEvidenceActionsContext);
  const linkClass = 'text-xs font-semibold text-brand-600 underline decoration-dotted underline-offset-2';
  // 결과표를 앞선 측정으로 옮기면 최신 결과에는 결과표가 없어 그 결과표를 볼 길이 사라졌다(dev 실측 2026-11-02 · 사용자 「권고대로」)
  const earlier = linkedResults.filter((r) => r.entryId !== entryId);
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span
        className={`${className} ${described.pendingEvidence ? 'text-amber-700' : 'text-ink-400'}`}
        data-testid={testId}
        data-pending-evidence={described.pendingEvidence ? 'true' : 'false'}
      >
        {described.text}
      </span>
      {/* 결과표는 «그 측정의 것» — 등록·보기를 결과 줄에서(측정 아닌 문서는 기존 「문서 등록」 그대로) */}
      {actions && canWriteRow(actions.canManage, row) && pendingResults.length > 0 && (
        <button type="button" className={linkClass} onClick={() => actions.onAttach(row, pendingResults)}>
          결과표 등록
        </button>
      )}
      {actions && evidenceRegistered && (
        <button type="button" className={linkClass} onClick={() => actions.onOpen(row, entryId, pendingResults)}>
          이어진 결과표 보기
        </button>
      )}
    </span>
      {earlier.map((r) => {
        const summary = describeMeasurementResult(r, row.intervalVariants);
        return (
          <span key={r.entryId} className="inline-flex flex-wrap items-center gap-1.5" data-testid="earlier-linked-result">
            <span className="text-xs text-ink-400">앞선 측정 {summary}</span>
            {actions && (
              <button
                type="button"
                className={linkClass}
                aria-label={`${summary} 측정에 이어진 결과표 보기`}
                onClick={() => actions.onOpen(row, r.entryId, pendingResults)}
              >
                이어진 결과표 보기
              </button>
            )}
          </span>
        );
      })}
    </span>
  );
}

/** 대상 하나의 지금 기간 상태 — 현장 전체 칸(`CurrentPeriodStatus`)과 같은 표·같은 가름 */
/**
 * 같은 파일을 함께 등록할 수 있는 다른 사건 항목(Q14 ⓐ — 사용자 결정 2026-11-04) — 같은 행(의무)의 사건 중 **문서가 아직 없는** 것(요청한 항목 제외).
 * 라벨 = 사건일 · 대상(설비 대상이면 「부모 › 이름」 — 서버 라벨 그대로) · 사건 내용. 서버가 같은 현장·같은 의무만 받는다(그 판정이 정본이다)
 */
export function eventSharedTargets(row: LegalObligationRow, instanceId: string): EvidenceSharedTarget[] {
  const subjectLabel = new Map(row.subjects.map((s) => [s.subjectKey, s.label]));
  return row.events
    .filter((e) => e.instance.instanceId !== instanceId && e.instance.evidenceCount === 0)
    .map((e) => ({
      instanceId: e.instance.instanceId,
      label: [formatDateToLocal(new Date(e.occurredAt)), subjectLabel.get(e.instance.subjectKey), e.detail].filter(Boolean).join(' · '),
    }));
}

function SubjectStatus({ subject }: { subject: LegalObligationRow['subjects'][number] }) {
  const instance = subject.currentInstance;
  if (subject.excludedReason) {
    return (
      <Tooltip content={SUBJECT_EXCLUDED_HINT[subject.excludedReason]}>
        <span className="cursor-help text-ink-500 underline decoration-dotted underline-offset-2" data-testid="subject-excluded">
          {subjectExcludedText({ excludedReason: subject.excludedReason, pendingFieldLabels: subject.pendingFieldLabels })}
        </span>
      </Tooltip>
    );
  }
  if (!instance) {
    const missing = describeMissingInstance(subject.currentPeriod);
    return (
      <Tooltip content={missing.hint}>
        <span className="cursor-help text-ink-500 underline decoration-dotted underline-offset-2">
          {missing.label}
          {missing.deadline ? ` · 기한 ${formatDateToLocal(new Date(missing.deadline))} 예정` : ''}
        </span>
      </Tooltip>
    );
  }
  const { label, tone } = INSTANCE_STATUS_LABEL[instance.status];
  // 면제(2026-11-04)가 이 기간을 닫았으면 이행과 갈라 보인다 — 근거 조항이 라벨이다(다른 현장에서 기록한 면제도 서버가 싣는다)
  const exemption = instance.exemptionBasis ? exemptionBasisDefinition(instance.exemptionBasis) : undefined;
  return (
    <>
      {instance.exemptionBasis ? (
        <Tooltip content={exemption?.description ?? instance.exemptionBasis}>
          <span className="inline-flex" data-testid="subject-exemption">
            <Badge tone="slate" size="sm" dot>
              면제 · {exemption?.label ?? instance.exemptionBasis}
            </Badge>
          </span>
        </Tooltip>
      ) : (
        <Badge tone={tone} size="sm" dot>
          {label}
        </Badge>
      )}
      <span className="text-ink-500">기한 {formatDateToLocal(new Date(instance.deadline))}</span>
      <PlannedNote plannedAt={instance.plannedAt} />
    </>
  );
}

/**
 * 대상 줄의 「확인 필요」(2026-11-04) — 사람 사실(의사 여부)이 비어 짧은 기한을 적용했다. 입력 입구는 조직 역할의 직위 배정이다
 * (문구의 정본은 `describeIntervalConfirmation`).
 */
function SubjectConfirmationNote({ subject }: { subject: LegalObligationSubjectView }) {
  const first = subject.intervalConfirmations?.[0];
  if (!first) return null;
  const { label, hint } = describeIntervalConfirmation(first);
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="subject-confirmation">
      <Tooltip content={hint}>
        <span className="cursor-help text-amber-700 underline decoration-dotted underline-offset-2">{label}</span>
      </Tooltip>
      {first.axis === 'PERSON_FACT' && (
        <Link to="/settings/roles" className="font-semibold text-brand-600 underline decoration-dotted underline-offset-2">
          직위 배정에서 입력
        </Link>
      )}
    </span>
  );
}

/**
 * 대상 줄의 쓰기 입구(2026-11-04) — 그 사람의 기간에 「문서 등록」 · 그 사람의 직위에 성립하는 근거 조항이 있으면 「면제 등록」(권한 `obligation.exempt`). 기간이 서지 않은
 * 대상(위탁·판정 미완·기간 없음)에는 그리지 않는다. 쓰기 막힘(`writeBlock`)은 행 단위 판정 그대로다
 */
function SubjectActionButtons({ row, subject }: { row: LegalObligationRow; subject: LegalObligationSubjectView }) {
  const actions = useContext(SubjectActionsContext);
  if (!actions || subject.excludedReason || row.exclusion) return null;
  if (subject.currentPeriod.kind !== 'period' && !subject.currentInstance) return null;
  const linkClass = 'text-xs font-semibold text-brand-600 underline decoration-dotted underline-offset-2';
  const exempted = !!subject.currentInstance?.exemptionBasis;
  const canAttach = canWriteRow(actions.canManage, row) && !exempted;
  // 근거 조항은 그 사람의 직위에 성립하는 것만(서버가 대상마다 싣는 `exemptionBases` — 2026-11-04). 비면 입구가 없다 —
  // 「보건관리자에게 신규교육 면제 근거가 없다」는 법이 그렇다는 뜻이라 안내 문구 없이 감춘다(풀릴 수 있는 막힘이 아니다)
  const canExempt = canWriteRow(actions.canExempt, row) && !exempted && (subject.exemptionBases?.length ?? 0) > 0;
  if (!canAttach && !canExempt) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {canAttach && (
        <button type="button" className={linkClass} aria-label={`${subject.label} 문서 등록`} onClick={() => actions.onAttach(row, subject)}>
          문서 등록
        </button>
      )}
      {canExempt && (
        <button type="button" className={linkClass} aria-label={`${subject.label} 면제 등록`} onClick={() => actions.onExempt(row, subject)}>
          면제 등록
        </button>
      )}
    </span>
  );
}

/**
 * 지금 기간의 계획일(②-c E8) — 입력은 연간 보기의 칸(「계획」)이다. 계획은 이행이 아니다(상태 배지가 이행을 말한다).
 * ⚠️ 이행이 끝난 칸에도 남긴다 — 계획서의 「계획 ○ / 실적 ●」가 둘을 나란히 적는다
 */
function PlannedNote({ plannedAt }: { plannedAt: string | null }) {
  if (!plannedAt) return null;
  return (
    <span className="text-xs text-ink-500" data-testid="planned-note">
      계획 {formatDateToLocal(new Date(plannedAt))}
    </span>
  );
}

function CurrentPeriodStatus({ row }: { row: LegalObligationRow }) {
  const instance = row.currentInstance;
  if (!instance) {
    const missing = describeMissingInstance(row.currentPeriod);
    return (
      <>
        <Tooltip content={missing.hint}>
          <span className="cursor-help text-xs text-ink-500 underline decoration-dotted underline-offset-2">
            {missing.label}
          </span>
        </Tooltip>
        {missing.deadline && (
          <span className="text-xs text-ink-400">
            기한 {formatDateToLocal(new Date(missing.deadline))} 예정
          </span>
        )}
      </>
    );
  }
  const { label, tone } = INSTANCE_STATUS_LABEL[instance.status];
  return (
    <>
      <Badge tone={tone} size="sm" dot>
        {label}
      </Badge>
      <span className="text-xs text-ink-500">
        기한 {formatDateToLocal(new Date(instance.deadline))}
      </span>
      <PlannedNote plannedAt={instance.plannedAt} />
    </>
  );
}

/**
 * 등록된 사건(9-i) — 사건마다 상태·기한 + 그 항목의 문서 등록·보기·등록 취소. 서버가 미완 전부 + 최근 완료 몇 건을
 * 사건일 내림차순으로 싣는다. 등록 취소는 그 항목에 문서가 없을 때만 보인다(서버도 문서가 있으면 400).
 */
function EventList({
  row,
  canManage,
  handlers,
}: {
  row: LegalObligationRow;
  canManage: boolean;
  handlers: EventHandlers;
}) {
  return (
    <ul className="space-y-1.5" aria-label={`${agencyDisplayTitle(row)} 등록된 사건`}>
      {row.events.map((event) => {
        const { label, tone } = INSTANCE_STATUS_LABEL[event.instance.status];
        return (
          <li key={event.entryId} className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="font-semibold text-ink-700">
              {formatDateToLocal(new Date(event.occurredAt))} 사건
            </span>
            {/* 출처(사용자 결정 2026-11-02) — 측정 결과가 낳은 사건만 표식(사람 등록은 표식 없음 · 기본) */}
            {event.source === 'MEASUREMENT_RESULT' && (
              <span title="측정 결과(노출기준 초과)를 기록할 때 자동으로 등록된 사건입니다." data-testid="event-source-auto">
                <Badge tone="slate" size="sm">
                  결과에서 자동
                </Badge>
              </span>
            )}
            {/* M4 ⓐ — 건강진단 결과표 수령 기록(사후관리 조치 소견)이 낳은 사건 */}
            {event.source === 'HEALTH_EXAM_RESULT' && (
              <span title="건강진단 결과표 수령을 기록할 때(사후관리 조치 소견) 자동으로 등록된 사건입니다." data-testid="event-source-auto">
                <Badge tone="slate" size="sm">
                  결과표에서 자동
                </Badge>
              </span>
            )}
            <Badge tone={tone} size="sm" dot>
              {label}
            </Badge>
            <span className="text-ink-500">기한 {formatDateToLocal(new Date(event.instance.deadline))}</span>
            {event.choiceMissing && (
              // ②-g2 G5 — 과정 구분 없이 등록된 옛 사건: 둘째 기한(3개월) 항목이 서지 않았다. 어느 쪽으로도 접지 않는다(사용자 결정)
              <Tooltip content="과정 구분(16시간 · 2시간) 없이 등록된 사건입니다. 16시간 과정이면 「잔여 12시간 — 3개월 이내」 항목이 따로 필요합니다. 등록을 취소하고 과정을 골라 다시 등록하세요.">
                <span className="cursor-help">
                  <Badge tone="amber" size="sm">
                    확인 필요
                  </Badge>
                </span>
              </Tooltip>
            )}
            {event.detail && (
              <Tooltip content={event.detail}>
                <span className="cursor-help text-ink-400 underline decoration-dotted underline-offset-2">내용</span>
              </Tooltip>
            )}
            <button
              type="button"
              onClick={() => handlers.onOpenEventEvidences(row, event)}
              className="rounded-[6px] px-1 font-semibold text-brand-600 underline decoration-dotted underline-offset-2 hover:bg-brand-50"
              aria-label={`${formatDateToLocal(new Date(event.occurredAt))} 사건 문서 ${event.instance.evidenceCount}건 보기`}
            >
              문서 {event.instance.evidenceCount}건
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => handlers.onAttachEventEvidence(row, event)}
                className="rounded-[6px] px-1 font-semibold text-brand-600 hover:bg-brand-50"
              >
                문서 등록
              </button>
            )}
            {canManage && event.instance.evidenceCount === 0 && (
              <button
                type="button"
                onClick={() => handlers.onRevokeEvent(row, event)}
                className="rounded-[6px] px-1 text-ink-500 hover:bg-ink-50 hover:text-ink-900"
              >
                등록 취소
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * 적용 판정 배지 — `UNKNOWN`일 때만 그린다(`APPLIES`는 표시하지 않는다: 목록에 있는 것 자체가 그 뜻이다).
 * 제외된 행엔 그리지 않는다 — 업체가 이미 「해당 없음」으로 닫은 질문이다.
 */
function ApplicabilityBadge({ row }: { row: LegalObligationRow }) {
  const badge = APPLICABILITY_BADGE[row.applicability];
  if (!badge || row.exclusion) return null;
  return (
    <span className="mt-1 inline-flex" title={badge.hint}>
      <Badge tone="amber" size="sm">
        {badge.label}
      </Badge>
    </span>
  );
}

/**
 * 증빙 칸 — 인스턴스가 있으면 개수를 **버튼**으로(누르면 목록 모달), 없으면 「—」. 제외된 행도 과거 증빙은
 * 볼 수 있어야 하므로 제외 여부로 가리지 않는다(붙이기만 막는다 — `EvidenceAction`).
 */
function EvidenceCell({
  row,
  onOpenEvidences,
}: {
  row: LegalObligationRow;
  onOpenEvidences: (row: LegalObligationRow) => void;
}) {
  const instance = row.currentInstance;
  if (!instance) return <span className="text-ink-400">—</span>;
  return (
    <button
      type="button"
      onClick={() => onOpenEvidences(row)}
      className="rounded-[8px] px-2 py-0.5 text-sm font-semibold text-brand-600 underline decoration-dotted underline-offset-2 hover:bg-brand-50"
      aria-label={`${agencyDisplayTitle(row)} 문서 ${instance.evidenceCount}건 보기`}
    >
      {instance.evidenceCount}건
    </button>
  );
}

/**
 * 증빙 추가 — `obligation.manage`가 있고 제외되지 않은 행에만. 인스턴스가 없어도 보인다(누르면 온디맨드
 * 실체화가 먼저 돈다 — 페이지의 `openAttachEvidence`).
 */
function EvidenceAction(props: {
  row: LegalObligationRow;
  canManage: boolean;
  onAttachEvidence: (row: LegalObligationRow) => void;
  handlers: EventHandlers;
}) {
  const { row, canManage, handlers } = props;
  if (!canManage || row.exclusion) return null;
  // ②-c1b — 결과가 다음 주기를 바꾸는 현장 단위 의무(특수건강진단 등)는 문서 등록 옆에 「결과 기록」 — 측정 대상 쌍은 현장 사실 명부가 입구다
  const resultEntry = acceptsSiteLevelResult(row);
  // M4 ⓐ — 특수·수시·임시건강진단은 「결과표 수령 기록」(사후관리 소견 → 조치결과 보고 사건). 일반·배치전은 보고 대상이 아니라 없다(서버도 400)
  const receiptEntry = HEALTH_EXAM_RECEIPT_OBLIGATION_KEYS.has(row.key);
  if (!resultEntry && !receiptEntry) return <EvidenceActionMain {...props} />;
  return (
    <>
      <EvidenceActionMain {...props} />
      {resultEntry && (
        <Button size="sm" variant="secondary" onClick={() => handlers.onRecordResult(row)}>
          결과 기록
        </Button>
      )}
      {receiptEntry && (
        <Button size="sm" variant="secondary" onClick={() => handlers.onRecordHealthExamReceipt(row)}>
          결과표 수령 기록
        </Button>
      )}
    </>
  );
}

/** 문서·사건·기준일 입구(주기 종류별) — 게이트(권한·제외)는 `EvidenceAction`이 이미 지났다 */
function EvidenceActionMain({
  row,
  onAttachEvidence,
  handlers,
}: {
  row: LegalObligationRow;
  canManage: boolean;
  onAttachEvidence: (row: LegalObligationRow) => void;
  handlers: EventHandlers;
}) {
  // 9-i — 사건 문서는 달력의 「이번 기간」이 없다. 사건을 등록하면 그 사건의 항목에 문서를 등록한다(사건 목록의 버튼)
  if (row.recurrence === 'ON_EVENT') {
    // ②-g2 G5 — 다른 문서의 사건을 따라 생기는 문서(특별교육 잔여 12시간)는 사건을 직접 넣지 않는다(서버도 400)
    if (row.eventSourceObligationKey) {
      return <span className="text-xs text-ink-500">원래 문서의 사건 등록으로 생깁니다</span>;
    }
    // 2026-11-04 — 사람 대상 사건 문서(직무 신규교육)는 직위 배정이 사건이다: 사람마다 기한이 서고 대상 줄에서 문서·면제를 등록한다(서버도 400)
    if (row.currentPeriod.kind === 'subjects') {
      return <span className="text-xs text-ink-500">직위 배정일부터 사람마다 기한이 정해집니다</span>;
    }
    return (
      <Button size="sm" variant="primary" onClick={() => handlers.onRegisterEvent(row)}>
        사건 등록
      </Button>
    );
  }
  // 9-l — ROLLING 주기는 기준일이 있어야 이번 주기가 선다. 없으면 「문서 등록」 대신 입력을 먼저 권한다
  if (isRollingRecurrence(row.recurrenceRule)) {
    // 대상별 의무(사람·설비·공정)는 기준일이 **대상마다**(배정일·설치일·결과)라 행 단위 「마지막 실시일 입력」이 없다 — 종전엔 그 버튼이 현장
    // 전체 키로 저장돼 아무 대상도 읽지 않았다(2026-11-03 S13 · 서버도 대상 키 없는 입력을 400으로 거부한다). 대상 줄이 각자 상태를 그린다
    if (row.currentPeriod.kind === 'subjects') return null;
    const hasPeriod = row.currentPeriod.kind === 'period';
    return (
      <>
        <Button size="sm" variant={hasPeriod ? 'secondary' : 'primary'} onClick={() => handlers.onSetLastPerformed(row)}>
          {row.rollingAnchor ? '실시일 수정' : '마지막 실시일 입력'}
        </Button>
        {hasPeriod && (
          <Button size="sm" variant="primary" onClick={() => onAttachEvidence(row)}>
            문서 등록
          </Button>
        )}
      </>
    );
  }
  return (
    <Button size="sm" variant="primary" onClick={() => onAttachEvidence(row)}>
      문서 등록
    </Button>
  );
}

function TemplateCell({ row }: { row: LegalObligationRow }) {
  if (row.templateAvailable) return <span className="text-ink-700">있음</span>;
  return (
    <Tooltip content="이 문서에 쓸 SafetyHub 양식은 추가될 예정입니다." align="right">
      <span className="cursor-help text-ink-400 underline decoration-dotted underline-offset-2">
        양식 추가 예정
      </span>
    </Tooltip>
  );
}

function ExclusionAction({
  row,
  canExempt,
  onExclude,
  onRevoke,
}: {
  row: LegalObligationRow;
  canExempt: boolean;
  onExclude: (row: LegalObligationRow) => void;
  onRevoke: (row: LegalObligationRow) => void;
}) {
  if (!canExempt) return null;
  if (row.exclusion) {
    return (
      <Button size="sm" variant="secondary" onClick={() => onRevoke(row)}>
        제외 해제
      </Button>
    );
  }
  return (
    <Button size="sm" variant="ghost" onClick={() => onExclude(row)}>
      해당 없음
    </Button>
  );
}
